import React from 'react';

import { ISO_DATE_FORMAT, ISO_MONTH_FORMAT } from '@gasbi/shared';
import { NotAuthorizedView, NotFound } from '@gasbi/shared';
import { ErrorView as Error } from '@gasbi/shared';
import { SectorBridgeActPreview } from '@gasbi/shared/web/Gds';
import dayjs from 'dayjs';
import { Navigate, createBrowserRouter } from 'react-router-dom';

import { UserPermissions } from '@/features/permissions';
import { Pages } from '@/pages';
import { organizationRoutes } from '@/pages/MyOrganization/routes';
import type { AppRouteObject } from '@/routeTypes';

import { buildingProfileActDetailsRoutes } from './pages/BuildingProfileActDetails/routes';
import { buildingProfileDetailsRoutes } from './pages/BuildingProfileDetails/routes';
import { gdsGasDistributionStationDetailsRoutes } from './pages/GdsGasDistributionStationDetails/routes';
import { gdsGasRoutesRoutes } from './pages/GdsGasRoutes/routes';
import { gdsGasThreadDetailsRoutes } from './pages/GdsGasThreadDetails/routes';
import { gdsSectorBridgeDetailsRoutes } from './pages/GdsSectorBridgeDetails/routes';
import { gdsSectorDetailsRoutes } from './pages/GdsSectorDetails/routes';
import { meteringPointActDetailsRoutes } from './pages/MeteringPointActDetails/routes';
import { personalAccountDetailsRoutes } from './pages/PersonalAccountDetails/routes';

const Router = React.lazy(() => import('@/layout/Router'));
const AuthLanding = React.lazy(() => import('@/pages/auth/AuthCallback'));
const ChangePassword = React.lazy(() => import('@/pages/auth/ChangePassword'));
const NoPermissions = React.lazy(() => import('@/pages/NoPermissions'));
const PersonalAccounts = React.lazy(() => import('@/pages/PersonalAccounts'));
const PersonalAccountDetails = React.lazy(
  () => import('@/pages/PersonalAccountDetails'),
);
const PersonalAccountHistory = React.lazy(
  () => import('@/pages/PersonalAccountHistory'),
);
const PersonalAccountArchive = React.lazy(
  () => import('@/pages/PersonalAccountArchive'),
);
const UnallocatedMeterReadings = React.lazy(
  () => import('@/pages/UnallocatedMeterReadings'),
);
const MeterReadingsDetails2 = React.lazy(
  () =>
    import('@/pages/UnallocatedMeterReadings/components/MeterReadingsDetails'),
);
const BuildingProfileDetails = React.lazy(
  () => import('@/pages/BuildingProfileDetails'),
);
const BuildingProfileNodeArchive = React.lazy(
  () => import('@/pages/BuildingProfileNodeArchive'),
);
const OperationalConsumption = React.lazy(
  () => import('@/pages/OperationalConsumption'),
);
const ActualConsumption = React.lazy(() => import('@/pages/ActualConsumption'));
const DataCorrection = React.lazy(() => import('@/pages/DataCorrection'));
const OrderedCapacity = React.lazy(() => import('@/pages/OrderedCapacity'));
const BuildingProfiles = React.lazy(() => import('@/pages/BuildingProfiles'));

const MeteringPointActForm = React.lazy(
  () => import('@/pages/MeteringPointActForm'),
);

const BuildingProfileActForm = React.lazy(
  () => import('@/pages/BuildingProfileActForm'),
);

const BuildingProfileActDetails = React.lazy(
  () => import('@/pages/BuildingProfileActDetails'),
);

const MeteringPointActDetails = React.lazy(
  () => import('@/pages/MeteringPointActDetails'),
);

const personalAccountRead = UserPermissions.PersonalAccountsPersonalAccountRead;
// An act form also reads its account (or building) and the acts: the Manage alone would open it onto 404s (GSB-8308).
const personalAccountActForm = [
  personalAccountRead,
  UserPermissions.PersonalAccountsActsRead,
  UserPermissions.PersonalAccountsActsManage,
];
const buildingProfileRead = UserPermissions.BuildingProfilesBuildingProfileRead;
const buildingProfileActForm = [
  buildingProfileRead,
  UserPermissions.BuildingProfilesActsRead,
  UserPermissions.BuildingProfilesActsManage,
];

const UndistributedPayments = React.lazy(
  () => import('@/pages/UndistributedPayments'),
);
const PaymentImports = React.lazy(() => import('@/pages/PaymentImports'));
const PaymentImportDetails = React.lazy(
  () => import('@/pages/PaymentImportDetails'),
);

const GdsGasDistributionStations = React.lazy(
  () => import('@/pages/GdsGasDistributionStations'),
);

const GdsGasDistributionStationDetails = React.lazy(
  () => import('@/pages/GdsGasDistributionStationDetails'),
);

const GdsGasThreads = React.lazy(() => import('@/pages/GdsGasThreads'));

const GdsGasThreadDetails = React.lazy(
  () => import('@/pages/GdsGasThreadDetails'),
);

const GdsSectors = React.lazy(() => import('@/pages/GdsSectors'));

const GdsSectorDetails = React.lazy(() => import('@/pages/GdsSectorDetails'));

const GdsSectorBridges = React.lazy(() => import('@/pages/GdsSectorBridges'));

const GdsSectorBridgeDetails = React.lazy(
  () => import('@/pages/GdsSectorBridgeDetails'),
);

const GdsSectorBridgeActs = React.lazy(
  () => import('@/pages/GdsSectorBridgeActs'),
);

const GdsGasRoutes = React.lazy(() => import('@/pages/GdsGasRoutes'));

const GdsOperatingGasRouteDetails = React.lazy(
  () => import('@/pages/GdsOperatingGasRouteDetails'),
);

export const routes: AppRouteObject[] = [
  ...organizationRoutes,
  {
    path: Pages.PERSONAL_ACCOUNTS,
    element: <PersonalAccounts />,
    handle: { permissions: personalAccountRead },
  },
  {
    path: `${Pages.PERSONAL_ACCOUNTS}/:personalAccountId`,
    element: <PersonalAccountDetails />,
    children: personalAccountDetailsRoutes,
    handle: { permissions: personalAccountRead },
  },
  {
    path: `${Pages.PERSONAL_ACCOUNTS}/:personalAccountId/${Pages.HISTORY}`,
    element: <PersonalAccountHistory />,
    handle: { permissions: personalAccountRead },
  },
  {
    path: `${Pages.PERSONAL_ACCOUNTS}/:personalAccountId/${Pages.ARCHIVE}`,
    element: <PersonalAccountArchive />,
    handle: {
      permissions: [
        personalAccountRead,
        UserPermissions.PersonalAccountsMeteringNodeRead,
      ],
    },
  },
  {
    path: `${Pages.PERSONAL_ACCOUNTS}/:personalAccountId/${Pages.METERING_POINT_ACT}/form`,
    element: <MeteringPointActForm />,
    handle: { permissions: personalAccountActForm },
  },
  {
    path: `${Pages.PERSONAL_ACCOUNTS}/:personalAccountId/${Pages.METERING_POINT_ACT}/form/:meteringPointActId`,
    element: <MeteringPointActForm isEditMode />,
    handle: { permissions: personalAccountActForm },
  },
  {
    path: `${Pages.PERSONAL_ACCOUNTS}/:personalAccountId/${Pages.METERING_POINT_ACT}/:meteringPointActId`,
    element: <MeteringPointActDetails />,
    children: meteringPointActDetailsRoutes,
    handle: {
      permissions: [
        personalAccountRead,
        UserPermissions.PersonalAccountsActsRead,
      ],
    },
  },
  {
    path: `${Pages.BUILDING_PROFILES}`,
    element: <BuildingProfiles />,
    handle: { permissions: buildingProfileRead },
  },
  {
    path: `${Pages.BUILDING_PROFILES}/:buildingProfileId`,
    element: <BuildingProfileDetails />,
    children: buildingProfileDetailsRoutes,
    handle: { permissions: buildingProfileRead },
  },
  {
    path: `${Pages.BUILDING_PROFILES}/:buildingProfileId/${Pages.ARCHIVE}`,
    element: <BuildingProfileNodeArchive />,
    handle: {
      permissions: [
        buildingProfileRead,
        UserPermissions.BuildingProfilesMeteringNodeRead,
      ],
    },
  },
  {
    path: `${Pages.BUILDING_PROFILES}/:buildingProfileId/${Pages.DOCUMENTS_AND_ACTS}/form`,
    element: <BuildingProfileActForm />,
    handle: { permissions: buildingProfileActForm },
  },
  {
    path: `${Pages.BUILDING_PROFILES}/:buildingProfileId/${Pages.DOCUMENTS_AND_ACTS}/form/:buildingProfileActId`,
    element: <BuildingProfileActForm isEditMode />,
    handle: { permissions: buildingProfileActForm },
  },
  {
    path: `${Pages.BUILDING_PROFILES}/:buildingProfileId/${Pages.DOCUMENTS_AND_ACTS}/:buildingProfileActId`,
    element: <BuildingProfileActDetails />,
    children: buildingProfileActDetailsRoutes,
    handle: {
      permissions: [
        buildingProfileRead,
        UserPermissions.BuildingProfilesActsRead,
      ],
    },
  },

  {
    path: Pages.UNALLOCATED_METER_READINGS,
    handle: {
      permissions: UserPermissions.MeterReadingsUnallocatedMeterReadingsRead,
    },
    element: (
      <Navigate to={dayjs().startOf('month').format(ISO_DATE_FORMAT)} replace />
    ),
  },
  {
    path: `${Pages.UNALLOCATED_METER_READINGS}/:date`,
    element: <UnallocatedMeterReadings />,
    handle: {
      permissions: UserPermissions.MeterReadingsUnallocatedMeterReadingsRead,
    },
    children: [
      {
        path: ':id',
        element: <MeterReadingsDetails2 />,
      },
    ],
  },
  {
    path: Pages.OPERATIONAL_CONSUMPTION,
    handle: {
      permissions:
        UserPermissions.OperationalAccountingOperationalConsumptionRead,
    },
    element: <Navigate to={dayjs().format(ISO_DATE_FORMAT)} replace />,
  },
  {
    path: `${Pages.OPERATIONAL_CONSUMPTION}/:date`,
    element: <OperationalConsumption />,
    handle: {
      permissions:
        UserPermissions.OperationalAccountingOperationalConsumptionRead,
    },
  },
  {
    path: Pages.ACTUAL_CONSUMPTION,
    element: <ActualConsumption />,
    handle: {
      permissions: UserPermissions.ActualAccountingActualConsumptionRead,
    },
  },
  {
    path: Pages.DATA_CORRECTION,
    handle: { permissions: UserPermissions.ActualAccountingDataCorrectionRead },
    element: <Navigate to={dayjs().format(ISO_MONTH_FORMAT)} replace />,
  },
  {
    path: `${Pages.DATA_CORRECTION}/:date`,
    element: <DataCorrection />,
    handle: { permissions: UserPermissions.ActualAccountingDataCorrectionRead },
  },
  {
    path: Pages.ORDERED_CAPACITY,
    element: <OrderedCapacity />,
    handle: {
      permissions: UserPermissions.ActualAccountingOrderedCapacityRead,
    },
  },
  {
    path: Pages.UNDISTRIBUTED_PAYMENTS,
    element: <Navigate to={dayjs().format(ISO_MONTH_FORMAT)} replace />,
  },
  {
    path: `${Pages.UNDISTRIBUTED_PAYMENTS}/:date`,
    element: <UndistributedPayments />,
  },
  {
    path: Pages.PAYMENT_IMPORTS,
    element: <Navigate to={dayjs().format(ISO_MONTH_FORMAT)} replace />,
    handle: { permissions: UserPermissions.FinanceUndistributedPaymentsRead },
  },
  {
    path: `${Pages.PAYMENT_IMPORTS}/:date`,
    element: <PaymentImports />,
    handle: { permissions: UserPermissions.FinanceUndistributedPaymentsRead },
  },
  {
    path: `${Pages.PAYMENT_IMPORTS}/:date/:importId`,
    element: <PaymentImportDetails />,
    handle: { permissions: UserPermissions.FinanceUndistributedPaymentsRead },
  },
  {
    path: Pages.GAS_DISTRIBUTION_STATIONS,
    element: <GdsGasDistributionStations />,
  },
  {
    path: `${Pages.GAS_DISTRIBUTION_STATIONS}/:stationId`,
    element: <GdsGasDistributionStationDetails />,
    children: gdsGasDistributionStationDetailsRoutes,
  },
  {
    path: `${Pages.GAS_DISTRIBUTION_STATIONS}/:stationId/${Pages.GAS_THREADS}/:gasThreadId`,
    element: <GdsGasThreadDetails />,
    children: gdsGasThreadDetailsRoutes,
  },
  {
    path: Pages.GAS_THREADS,
    element: <GdsGasThreads />,
  },
  {
    path: `${Pages.GAS_THREADS}/:gasThreadId`,
    element: <GdsGasThreadDetails />,
    children: gdsGasThreadDetailsRoutes,
  },
  {
    path: `${Pages.GAS_DISTRIBUTION_STATIONS}/:stationId/${Pages.GAS_THREADS}/:gasThreadId/${Pages.SECTORS}/:sectorId`,
    element: <GdsSectorDetails />,
    children: gdsSectorDetailsRoutes,
  },
  {
    path: `${Pages.GAS_THREADS}/:gasThreadId/${Pages.SECTORS}/:sectorId`,
    element: <GdsSectorDetails />,
    children: gdsSectorDetailsRoutes,
  },
  {
    path: Pages.SECTORS,
    element: <GdsSectors />,
  },
  {
    path: `${Pages.SECTORS}/:sectorId`,
    element: <GdsSectorDetails />,
    children: gdsSectorDetailsRoutes,
  },
  {
    path: Pages.SECTOR_BRIDGES,
    element: <GdsSectorBridges />,
  },
  {
    path: `${Pages.SECTOR_BRIDGES}/:sectorBridgeId`,
    element: <GdsSectorBridgeDetails />,
    children: gdsSectorBridgeDetailsRoutes,
  },
  {
    path: `${Pages.SECTOR_BRIDGES}/:sectorBridgeId/:sectorId`,
    element: <GdsSectorDetails />,
    children: gdsSectorDetailsRoutes,
  },
  {
    path: Pages.BRIDGE_ACTS,
    element: <GdsSectorBridgeActs />,
    children: [{ path: ':bridgeActId', element: <SectorBridgeActPreview /> }],
  },
  {
    path: Pages.GAS_ROUTES,
    element: <GdsGasRoutes />,
    children: gdsGasRoutesRoutes,
  },
  {
    path: `${Pages.GAS_ROUTES}/${Pages.OPERATING_GAS_ROUTES}/:operatingGasRouteId`,
    element: <GdsOperatingGasRouteDetails />,
  },
  {
    path: Pages.CHANGE_PASSWORD,
    element: <ChangePassword />,
  },
  { path: '*', element: <NotFound /> },
  { path: Pages.NOT_FOUND, element: <NotFound /> },
];

export const allRoutes = [
  {
    path: '/',
    element: <AuthLanding />,
  },
  {
    path: Pages.AUTH_CALLBACK,
    element: <AuthLanding />,
  },
  {
    path: Pages.NO_PERMISSIONS,
    element: <NoPermissions />,
  },
  {
    path: Pages.NOT_AUTHORIZED,
    element: <NotAuthorizedView />,
  },
  {
    path: '/:contextOrganizationId/*',
    element: <Router />,
    children: routes,
  },
  { path: '*', element: <NotFound /> },
  { path: Pages.NOT_FOUND, element: <NotFound /> },
];

export const getRoutes = () =>
  createBrowserRouter(
    allRoutes.map(route => ({ ...route, errorElement: <Error /> })),
  );
